import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { BotFlow, IntentHeadlineId, NlpModelId, StepNlpSettings } from '@feedyou-ai/feedbot-schema'
import arrayMove from 'array-move'
import { findIntent, findIntentHeadline, getNlpModelById } from './utils'
import {
  AddTestCase,
  AddIntentListItem,
  AddModelIntentRule,
  AddModelIntentUtterance,
  AddModelVariable,
  DeleteModelIntentDescription,
  DeleteModelIntentExample,
  DeleteModelIntentRule,
  DeleteModelIntentRuleByRegex,
  DeleteModelIntentUtterance,
  DeleteModelIntentVariable,
  IntentListAction,
  RenameModelIntent,
  SetModelIntentDialogId,
  UpdateModelIntent,
  UpdateModelIntentDescription,
  UpdateModelIntentExamples,
  UpdateModelIntentRule,
  UpdateModelIntentRuleByRegex,
  UpdateModelIntentUtterance,
  UpdateModelIntentVariable,
  UpdateModelIntentRulePriority,
  UpdateModelProvider
} from './types'

export const initialState: BotFlow = { id: '', version: '', dialogs: [] } as any

const slice = createSlice({
  name: 'botNlp',
  initialState,
  reducers: {
    addIntentListItem: (state, action: PayloadAction<AddIntentListItem>) => {
      const { modelId, item } = action.payload
      const model = getNlpModelById(state, modelId)

      if (!model) {
        return
      }

      if (!model.intents) {
        model.intents = [item]
        return
      }

      model.intents.unshift(item)
    },

    deleteIntentListItem: (state, action: PayloadAction<IntentListAction>) => {
      const { modelId, itemId } = action.payload

      const intentsWithHeadlines = getNlpModelById(state, modelId)?.intents

      const index = intentsWithHeadlines?.findIndex(item => item.id === itemId)

      if (index !== undefined) {
        intentsWithHeadlines?.splice(index, 1)
      }
    },

    renameModelIntent: (state, action: PayloadAction<RenameModelIntent>) => {
      const { modelId, itemId, newIntentName } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.name = newIntentName
      }
    },
    updateModelIntentDescription: (state, action: PayloadAction<UpdateModelIntentDescription>) => {
      const { modelId, itemId, newIntentDescription } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.description = newIntentDescription
      }
    },
    deleteModelIntentDescription: (state, action: PayloadAction<DeleteModelIntentDescription>) => {
      const { modelId, itemId } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.description = ''
      }
    },
    changeHeadlineText: (
      state,
      action: PayloadAction<{ modelId: NlpModelId; headlineId: IntentHeadlineId; newText: string }>
    ) => {
      const { modelId, newText, headlineId } = action.payload

      const model = getNlpModelById(state, modelId)
      const headline = findIntentHeadline(model?.intents, headlineId)

      if (headline) {
        headline.text = newText
      }
    },

    moveIntentListItem: (
      state,
      action: PayloadAction<{ from: number; to: number; modelId: string }>
    ) => {
      const { modelId, from, to } = action.payload

      const model = getNlpModelById(state, modelId)

      if (model?.intents) {
        model.intents = arrayMove(model.intents, from, to)
      }
    },

    disableModelIntent: (state, action: PayloadAction<IntentListAction>) => {
      const { modelId, itemId } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.disabled = true
      }
    },

    enableModelIntent: (state, action: PayloadAction<IntentListAction>) => {
      const { modelId, itemId } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.disabled = false
      }
    },

    addModelIntentVariable: (state, action: PayloadAction<AddModelVariable>) => {
      const { modelId, itemId, variableName, variableValue } = action.payload

      const model = getNlpModelById(state, modelId)?.intents
      const intent = findIntent(model, itemId)

      console.log('intent', JSON.parse(JSON.stringify(intent)))

      if (intent && !intent?.variables) {
        intent.variables = []
      }

      intent?.variables?.push({ name: variableName, value: variableValue || '' })
    },

    setSelectedDialog: (state, action: PayloadAction<SetModelIntentDialogId>) => {
      const { modelId, itemId, dialogId } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.dialog = dialogId
      }
    },

    addModelIntentRule: (state, action: PayloadAction<AddModelIntentRule>) => {
      const { rule, modelId, itemId } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent) {
        return
      }

      const presentRules = intent.rules || []

      intent.rules = [...presentRules, rule]
    },

    addModelIntentUtterance: (state, action: PayloadAction<AddModelIntentUtterance>) => {
      const { modelId, itemId, value } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent) {
        intent.utterances = [...(intent.utterances || []), value]
      }
    },
    updateModelIntent: (state, action: PayloadAction<UpdateModelIntent>) => {
      const { modelId, itemId, props } = action.payload

      const model = getNlpModelById(state, modelId)

      if (!model?.intents) {
        return
      }

      const indexIntent = model.intents.findIndex(({ id }) => id === itemId)

      if (indexIntent < 0) {
        return
      }

      const intent = model.intents[indexIntent]

      model.intents[indexIntent] = { ...intent, ...props }
    },

    updateModelIntentRule: (state, action: PayloadAction<UpdateModelIntentRule>) => {
      const { rule, modelId, itemId, ruleIndex } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.rules) {
        return
      }

      intent.rules[ruleIndex] = rule
    },

    updateModelIntentRuleByRegex: (state, action: PayloadAction<UpdateModelIntentRuleByRegex>) => {
      const { modelId, itemId, oldRegex, newRegex } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.rules) {
        return
      }

      intent.rules.forEach(rule => {
        if (rule.regex === oldRegex) {
          rule.regex = newRegex
        }
      })
    },

    updateModelIntentRulePriority: (
      state,
      action: PayloadAction<UpdateModelIntentRulePriority>
    ) => {
      const { modelId, itemId, regex, priority } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      intent?.rules &&
        intent.rules.forEach(rule => {
          if (rule.regex === regex) {
            rule.priority = priority
          }
        })
    },

    updateModelIntentVariable: (state, action: PayloadAction<UpdateModelIntentVariable>) => {
      const { modelId, itemId, value, name, mode } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent) {
        return
      }

      if (!intent.variables) {
        intent.variables = []
      }

      const variableAlreadyExists = intent.variables?.some(variable => variable.name === name)

      if (!variableAlreadyExists && mode === 'upsert') {
        intent.variables?.push({ name: name, value: value })
        return
      }

      intent.variables?.forEach(variable => {
        if (variable.name === name) {
          variable.value = value
        }
      })
    },

    updateModelIntentUtterance: (state, action: PayloadAction<UpdateModelIntentUtterance>) => {
      const { modelId, itemId, utteranceIndex, value } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent?.utterances) {
        intent.utterances[utteranceIndex] = value
      }
    },

    deleteModelIntentRule: (state, action: PayloadAction<DeleteModelIntentRule>) => {
      const { modelId, itemId, ruleIndex } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.rules) {
        return
      }

      intent.rules.splice(ruleIndex, 1)
    },

    deleteModelIntentRuleByRegex: (state, action: PayloadAction<DeleteModelIntentRuleByRegex>) => {
      const { modelId, itemId, regex } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.rules) {
        return
      }

      intent.rules = intent.rules.filter(rule => rule.regex !== regex)
    },

    deleteModelIntentVariable: (state, action: PayloadAction<DeleteModelIntentVariable>) => {
      const { modelId, itemId, name } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.variables) {
        return
      }

      intent.variables = intent.variables.filter(variable => variable.name !== name)
    },

    deleteModelIntentUtterance: (state, action: PayloadAction<DeleteModelIntentUtterance>) => {
      const { modelId, itemId, utteranceIndex } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent?.utterances) {
        intent.utterances.splice(utteranceIndex, 1)
      }
    },

    updateModelIntentExamples: (state, action: PayloadAction<UpdateModelIntentExamples>) => {
      const { modelId, itemId, oldValue, newValue } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (!intent?.utterances) {
        return
      }

      intent.utterances = intent?.utterances?.map(example => {
        if (example === oldValue) {
          return newValue
        }

        return example
      })
    },

    deleteModelIntentExample: (state, action: PayloadAction<DeleteModelIntentExample>) => {
      const { modelId, itemId, exampleName } = action.payload

      const model = getNlpModelById(state, modelId)
      const intent = findIntent(model?.intents, itemId)

      if (intent?.utterances) {
        const exampleIndex = intent.utterances.findIndex(example => example === exampleName)
        if (exampleIndex >= 0) {
          intent.utterances.splice(exampleIndex, 1)
        }
      }
    },

    updateStepNlpSettings: (state, action: PayloadAction<StepNlpSettings>) => {
      if (!state.nlp) {
        state.nlp = {}
      }

      state.nlp.stepNlp = action.payload
    },

    updateModelProvider: (state, action: PayloadAction<UpdateModelProvider>) => {
      const { modelId, provider } = action.payload

      const model = getNlpModelById(state, modelId)
      if (model) {
        model.provider = provider
      }
    },
    setIgnoredUtterance: (state, action: PayloadAction<{ index: number; utterance: string }>) => {
      const { index, utterance } = action.payload
      if (state?.nlp?.training?.ignoredUtterances?.[index]) {
        state.nlp.training.ignoredUtterances[index] = utterance
      }
    },
    deleteIgnoredUtterance: (state, action: PayloadAction<{ index: number }>) => {
      state?.nlp?.training?.ignoredUtterances?.splice(action.payload.index, 1)
    },
    addTestCase: (state, action: PayloadAction<AddTestCase>) => {
      const { modelId, itemId, utterance } = action.payload

      if (!state?.nlp?.test) return
      if (!state?.nlp?.test?.cases?.length) state.nlp.test.cases = []
      state.nlp.test.cases.push({ modelId, utterance, intentId: itemId })
    }
  }
})

export const reducer = slice.reducer
export const actions = slice.actions
